import { useEffect, useRef, useState } from 'react';
import { VIDEO_EXPORT_STREAM_THRESHOLD } from '@shared/constants';
import type { UploadProgressEvent } from '@/lib/uploadPipeline';
import type { ProgressStage } from '@/lib/uploadProgress';
import { stopAllPlayback } from '@/stores/playback';
import { requestCancelPipeline } from '@/lib/pipelineCoordinator';
import styles from './UploadModal.module.css';

/** Texte je Stufe des Byte-Modells (`src/lib/uploadProgress.ts`). Ein
 *  ruhiges Label statt rotierender Beruhigungstexte — den Fortschritt zeigt
 *  der Balken (2026-10-02). */
const STAGE_LABELS: Record<ProgressStage, string> = {
  preparing: 'Preparing files…',
  uploading: 'Uploading files…',
  // Hier steht der Balken bewusst still (der Dienst schreibt an die Cloud,
  // ohne Signal). Ein stehender Balken liest sich wie ein Problem — der
  // Zusatz sagt, dass das Warten dazugehört (Philip, 2026-10-02).
  saving: 'Saving to cloud, please wait…',
};

type ProgressView = { ratio: number; stage: ProgressStage; bytes: { sent: number; total: number } };
const PROGRESS_START: ProgressView = { ratio: 0, stage: 'preparing', bytes: { sent: 0, total: 0 } };

const fmtMB = (bytes: number): string => `${Math.round(bytes / 1024 / 1024)} MB`;

export interface UploadModalProps {
  open: boolean;
  nextVersion: number;
  readyCount: number;
  hasVideo: boolean;
  /** Größe des Admin-Cloud-Videos in Bytes (0 / undefined = keins). Nur
   *  oberhalb von `VIDEO_EXPORT_STREAM_THRESHOLD` fragt das Modal überhaupt
   *  nach — darunter geht das Premix-MP4 automatisch mit (Spec 2026-09-21
   *  § 7). `null` = Größe unbekannt → fragt ebenfalls (Integrations-Review
   *  2026-09-24; früher galt unbekannt als klein). */
  cloudVideoSize?: number | null;
  onClose(): void;
  /** Resolves when upload completes; callee owns the actual dispatch and
   *  is responsible for calling `onProgress(event)` to drive the modal's
   *  progress bar (`overall`) + status line. Throws on failure — modal catches and
   *  shows the error message inline (no confirm-toast on the modal
   *  itself). */
  onSubmit(args: {
    note: string | null;
    includeVideo: boolean;
    onProgress(event: UploadProgressEvent): void;
    /** Aborts all in-flight upload XHRs when fired. The X-button confirm
     *  flow triggers this; pipeline rejects with AbortError, the modal
     *  treats that as a clean cancel (no error toast). */
    signal: AbortSignal;
  }): Promise<void>;
}

export function UploadModal({
  open, nextVersion, readyCount, hasVideo, cloudVideoSize, onClose, onSubmit,
}: UploadModalProps) {
  const [note, setNote] = useState('');
  const [includeVideo, setIncludeVideo] = useState(false);
  // Großes Cloud-Video: jede Version lädt das komplette Video erneut hoch,
  // deshalb darf das Talent es hier abwählen — vorbelegt bleibt es an.
  const unknownCloudSize = cloudVideoSize === null;
  const bigCloudVideo = unknownCloudSize || (cloudVideoSize ?? 0) > VIDEO_EXPORT_STREAM_THRESHOLD;
  // F3 (Spec 2026-09-21): die Cloud-Video-Größe kommt per HEAD und kann erst
  // NACH einem bewussten Untick des Talents eintreffen — der Vorbelege-Effekt
  // unten feuerte dann erneut und tickte wieder an. Einmal pro Öffnung: sobald
  // das Talent die Checkbox selbst anfasst, lässt der Effekt sie in Ruhe.
  const videoCheckboxTouchedRef = useRef(false);
  // Nicht-fatale Hinweise aus der Pipeline (bisher: Mux gescheitert). Werden
  // in der Abschluss-Ansicht gezeigt; solange welche anliegen, schließt das
  // Modal nicht von selbst.
  const [warnings, setWarnings] = useState<string[]>([]);
  const warningsRef = useRef<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Final success state — replaces the dialog content with a clean "Upload
  // finished + checkmark" view. Avoids the brief flash where late progress
  // events would otherwise overwrite the busy-phase status line right before
  // close.
  const [finished, setFinished] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Echter Fortschritt aus `overall` (bytegewichtet, uploadProgress.ts).
  // XHR-Progress feuert viele Male pro Sekunde — der neueste Stand liegt
  // im Ref, gerendert wird höchstens einmal je Frame.
  const [progress, setProgress] = useState<ProgressView>(PROGRESS_START);
  const progressRef = useRef<ProgressView>(PROGRESS_START);
  const progressFrameRef = useRef<number | null>(null);
  const flushProgress = () => {
    progressFrameRef.current = null;
    setProgress(progressRef.current);
  };
  const pushProgress = (next: ProgressView) => {
    progressRef.current = next;
    if (progressFrameRef.current == null) progressFrameRef.current = window.requestAnimationFrame(flushProgress);
  };
  const resetProgress = () => {
    if (progressFrameRef.current != null) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
    progressRef.current = PROGRESS_START;
    setProgress(PROGRESS_START);
  };
  // Cancel-flow state: X-during-busy fires a native window.confirm (matches
  // "Start fresh" pattern); on OK we abort the AbortController and flip
  // `cancelling` so the status line explains the wait. Aborting a running
  // large-file PUT through server → cloud takes a moment to drain.
  const [cancelling, setCancelling] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Captured version number — frozen on open so the title doesn't flicker
  // to "Upload as Version N+1" between addVersion() and the parent
  // unmounting us during the close-on-success delay.
  const [displayVersion, setDisplayVersion] = useState(nextVersion);

  // Reset every time the modal opens.
  useEffect(() => {
    if (open) {
      setNote('');
      setIncludeVideo(false);
      videoCheckboxTouchedRef.current = false;
      setWarnings([]);
      warningsRef.current = [];
      setBusy(false);
      setFinished(false);
      setErrorMessage(null);
      resetProgress();
      setCancelling(false);
      abortControllerRef.current = null;
      // Freeze the version label at open-time. After upload completes,
      // addVersion() bumps the prop from N to N+1 while we're still
      // mounted (closing animation), which would otherwise flash the
      // wrong number in the title.
      setDisplayVersion(nextVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Die Checkbox für ein großes Cloud-Video ist vorbelegt an. Eigener Effekt,
  // weil die Größe per HEAD kommt und erst nach dem Öffnen eintreffen kann —
  // der Reset oben hat dann schon `false` gesetzt.
  useEffect(() => {
    if (open && bigCloudVideo && !videoCheckboxTouchedRef.current) setIncludeVideo(true);
  }, [open, bigCloudVideo]);

  // Esc to close (only when not busy).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  /** Apply a single pipeline event to local state. Pulled out so submit()
   *  can pass a stable handler down to the orchestrator. */
  const handleEvent = (e: UploadProgressEvent) => {
    switch (e.kind) {
      case 'overall':
        // Einzige Quelle des Balkens: Verhältnis, Stufe und Bytes aus dem
        // Byte-Modell der Pipeline (monoton, ehrlich, siehe uploadProgress.ts).
        pushProgress({ ratio: e.ratio, stage: e.stage, bytes: e.bytes });
        return;
      case 'warning':
        // Der Upload läuft weiter — der Hinweis erscheint am Ende, damit er
        // nicht vom Fortschritt überschrieben wird.
        warningsRef.current = [...warningsRef.current, e.text];
        setWarnings(warningsRef.current);
        return;
      case 'file-error':
        setErrorMessage(`Upload failed: ${e.error}`);
        return;
      case 'message':
      case 'file-start':
      case 'file-bytes':
      case 'file-done':
      case 'phase':
      case 'phase-plan':
      case 'mux-progress':
      case 'server-processing':
        // Telemetrie bzw. Konsumenten ohne Modal (UploadSummary nutzt
        // `message`). Der Balken hängt nur an `overall`.
        return;
    }
  };

  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    // Same intent as Next / Prev navigation: silence any active playback
    // (chips, timeline preview, version-bar, generate-all pipeline) so it
    // doesn't keep playing during/after the upload.
    requestCancelPipeline();
    stopAllPlayback();
    setBusy(true);
    setErrorMessage(null);
    setWarnings([]);
    warningsRef.current = [];
    resetProgress();
    abortControllerRef.current = new AbortController();
    try {
      await onSubmit({
        note: note.trim() || null,
        includeVideo,
        onProgress: handleEvent,
        signal: abortControllerRef.current.signal,
      });
      // onSubmit resolved → server actually wrote the version.
      // Switch to the finished view (clean checkmark + "Upload finished")
      // so any trailing pipeline events can't repaint the status line.
      setFinished(true);
      setBusy(false);
      // Brief success indication, then parent closes us via onClose. Liegt
      // ein Hinweis an (Mux gescheitert), bleibt die Ansicht stehen, bis das
      // Talent selbst schließt — sonst wäre die Warnung nach 800 ms weg.
      if (warningsRef.current.length === 0) setTimeout(onClose, 800);
    } catch (e) {
      // Clean cancel via X button — close silently, don't show error.
      // The pipeline rejected because we aborted the controller; the
      // caller didn't mark the upload as added to the version list, so
      // nothing to undo client-side. (Server-side partial files may
      // dangle; v2 fix planned for explicit cleanup.)
      if (abortControllerRef.current?.signal.aborted) {
        setBusy(false);
        onClose();
        return;
      }
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setErrorMessage(`Upload failed: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
      >
        {!finished && (
          <>
            <div className={styles.headerRow}>
              <h2 id="upload-modal-title" className={styles.title}>
                Upload as Version {displayVersion}
              </h2>
              <button
                type="button"
                className={styles.closeBtn}
                disabled={cancelling}
                onClick={() => {
                  if (cancelling) return;
                  if (busy) {
                    // Native browser confirm — matches the "Start fresh"
                    // pattern elsewhere in the app, no custom UI needed.
                    const ok = window.confirm(
                      'Cancel the upload? Any files already uploaded to TRO Cloud will be deleted.',
                    );
                    if (!ok) return;
                    setCancelling(true);
                    abortControllerRef.current?.abort();
                    // submit() catch path detects signal.aborted and closes
                    // the modal once all in-flight XHRs have drained.
                  } else onClose();
                }}
                aria-label={busy ? 'Cancel upload' : 'Close'}
              >
                <i className="ph-bold ph-x" aria-hidden="true" />
              </button>
            </div>
            <p className={styles.info}>
              {readyCount} block{readyCount !== 1 ? 's' : ''} with favorites will be uploaded
            </p>
          </>
        )}

        {finished && (
          <div className={styles.finishedWrap}>
            <i
              className={`ph-fill ph-cloud-check ${styles.finishedIcon}`}
              aria-hidden="true"
            />
            <div className={styles.finishedText}>Upload finished</div>
            {warnings.length > 0 && (
              <>
                <div className={styles.warningList} role="status">
                  {warnings.map((w, i) => (
                    <div key={i} className={styles.warningRow}>
                      <i className="ph-bold ph-warning" aria-hidden="true" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={onClose}
                >
                  Close
                </button>
              </>
            )}
          </div>
        )}

        {!busy && !finished && (
          <>
            <label className={styles.label} htmlFor="upload-note">Note (optional)</label>
            <textarea
              id="upload-note"
              className={styles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Adjusted pacing on block 2"
              disabled={busy}
              rows={3}
            />

            {(hasVideo || bigCloudVideo) && (
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={includeVideo}
                  onChange={(e) => {
                    videoCheckboxTouchedRef.current = true;
                    setIncludeVideo(e.target.checked);
                  }}
                  disabled={busy}
                />
                <span>Also upload the video</span>
              </label>
            )}
            {/* Nur beim großen Cloud-Video: erklärt, warum hier überhaupt
                gefragt wird (kleine Videos gehen ohne Rückfrage mit). */}
            {bigCloudVideo && !hasVideo && (
              <p className={styles.checkboxHint}>
                {unknownCloudSize
                  ? 'The size of this reference video could not be determined — every version uploads a full copy.'
                  : `This reference video is large (${Math.round((cloudVideoSize ?? 0) / 1024 / 1024)} MB) — every version uploads a full copy.`}
              </p>
            )}
          </>
        )}

        {busy && (
          <div className={styles.progressWrap}>
            {/* Wolke mit aufsteigenden Carets als Lebenszeichen — sie läuft
                auch, wenn der Balken ehrlich stillsteht (Stufe „saving“:
                der Dienst schreibt an die Cloud, ohne Signal). */}
            <div className={styles.uploadIconWrap} aria-hidden="true">
              <i className={`ph-fill ph-cloud ${styles.uploadIcon}`} />
              <div className={styles.uploadTrail}>
                <i className={`ph-bold ph-caret-up ${styles.uploadTrailDot} ${styles.uploadTrailDot1}`} />
                <i className={`ph-bold ph-caret-up ${styles.uploadTrailDot} ${styles.uploadTrailDot2}`} />
                <i className={`ph-bold ph-caret-up ${styles.uploadTrailDot} ${styles.uploadTrailDot3}`} />
              </div>
            </div>
            {/* Echter Balken: bytegewichtet aus `overall` (uploadProgress.ts). */}
            <div
              className={styles.bar}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress.ratio * 100)}
              aria-label="Upload progress"
            >
              <div className={styles.barTrack}>
                <div
                  className={`${styles.barFill} ${progress.stage === 'saving' ? styles.barFill_waiting : ''}`}
                  style={{ width: `${Math.round(progress.ratio * 1000) / 10}%` }}
                />
              </div>
              <div className={styles.barPercent}>{Math.round(progress.ratio * 100)}%</div>
            </div>
            <div
              className={`${styles.statusLine} ${errorMessage ? styles.statusLine_error : ''}`}
              aria-live="polite"
            >
              {cancelling
                ? 'Cancelling upload and cleaning up TRO Cloud…'
                : errorMessage ?? (
                  <>
                    {STAGE_LABELS[progress.stage]}
                    {progress.bytes.total > 0 && (
                      <span className={styles.statusBytes}> · {fmtMB(progress.bytes.sent)} / {fmtMB(progress.bytes.total)}</span>
                    )}
                  </>
                )}
            </div>
          </div>
        )}

        {!busy && !finished && errorMessage && (
          <div className={styles.errorLabel}>{errorMessage}</div>
        )}

        {!finished && (
          <div className={styles.actions}>
            {/* Cancel only makes sense before upload starts. While busy
                the close-X in the header stays disabled too — there's
                nothing for the user to do except wait. */}
            {!busy && (
              <button
                type="button"
                className={styles.btnGhost}
                onClick={onClose}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => void submit()}
              disabled={busy || readyCount === 0}
            >
              <i className="ph-bold ph-upload-simple" /> Upload Version
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
